import React, { Component } from 'react';
import { connect } from "react-redux";
import { Circle, Group, Stage, Layer, Rect, Line, Text} from 'react-konva';

const LINE_COLOR = "#f55442";
const FREQUENCY_FOR_X = {};
const X_FOR_FREQUENCY = {};
const WIDTH = 360;
const HEIGHT = 160;
const GUIDE_FREQUENCIES = [
    50, 60, 70, 80, 90, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 2000,
    3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000];

class EQCurve extends Component {
    constructor(props) {
        super(props);
        this.state = {
        };

        this.guideLines = this.getGuideLines();
        this.fillCache();
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.nextProps !== nextProps.nextProps) {
            return true;
        }

        if (JSON.stringify(this.props.equalizerPoints) !==
            JSON.stringify(nextProps.equalizerPoints)) {
            return true;
        }

        return false;
    }

    fillCache() {
        for (let i=0; i < 14000; i++) {
            this.getXPositionForFrequency(i);
        }

        for (let i=0; i < 650; i++) {
            this.getFrequencyForXPosition(i);
        }
    }

    getFrequencyForXPosition(x) {
        if (x in FREQUENCY_FOR_X) {
            return FREQUENCY_FOR_X[x];
        } 
            
        let sum=0;
        let i=0;
        let frequencySum = 0;
        for (i=0; sum < x; i++) {
            sum += this.props.getWidth(i);
            frequencySum += 86;
        }

        let width = this.props.getWidth(i);
        let beforeTotal = sum - width;
        let beforeX = x - beforeTotal;
        let frequency = frequencySum - 86 + 86.0 * (beforeX / width);

        FREQUENCY_FOR_X[x] = frequency;

        return frequency;
    }

    // Returns the x-position on the canvas for a frequency value
    getXPositionForFrequency(frequency) {
        if (frequency in X_FOR_FREQUENCY) {
            return X_FOR_FREQUENCY[frequency];
        }
        let i = 0;
        let sum = 0;
        let fSum = 0;
        for (i=0; fSum < frequency; i++) {
            sum += this.props.getWidth(i);
            fSum += 86;
        }

        sum -= this.props.getWidth(i);
        let freqRemainder =  frequency - (fSum - 86);
        sum += (freqRemainder / 86) * this.props.getWidth(i);

        X_FOR_FREQUENCY[frequency] = sum;
        return sum;
    }

    getGainYPosition(gain) {
        // TODO: use logorithm for calculating the gain
        // db Y position
        return (HEIGHT / 2) - gain * 5;
    }

    get equalizerPoints() {
        return this.props.effect.equalizerPoints || [];
    }

    // returns a curved line representing the EQ curve, using
    // the equalizer points defined by the user
    getEQCurve() {
        // Each equalizer point is a function and so the curve
        // is simply the addition of all these functions
        // at each x. i.e. f(x) = f_1(x) + f_2(x) + ... f_8(x)
        let pts = [];
        let equalizerPoints = this.equalizerPoints
            .filter(x => x.enabled);

        for (let i=0; i < WIDTH; i+=2) {
            let frequency = this.getFrequencyForXPosition(i);
            let gain = 0;
            for (let j in equalizerPoints) {
                gain += equalizerPoints[j].frequencyToGain(frequency);
            }
            pts.push(i);
            pts.push(this.getGainYPosition(gain));
        }

        return (
            <Line
              closed={false}
              strokeWidth={2}
              lineCap='butt'
              lineJoin='miter'
              tension={0.91}
              points={pts}
              stroke={LINE_COLOR}/>
        );
    }

    getEqualizerPoints() {
        let equalizerPoints = this.equalizerPoints
            .filter(eq => eq.enabled);
        return equalizerPoints.map(
            (pt, i) => {
                let pointObject = equalizerPoints[i];
                let frequency = this.getXPositionForFrequency(pt.frequency);
                return (
                    <Group>
                     <Circle
                        onMouseDown={(e) => this.startDraggingPoint(pointObject)}
                        onMouseUp={(e) => this.stopDraggingPoint(pointObject)}
                        x={frequency}
                        y={this.getGainYPosition(pt.gain)}
                        radius={8}
                        fill={this.props.selectedPoint === pointObject ? 'yellow' : 'black'}
                        stroke='yellow'
                        strokeWidth={2} />
                      <Text
                        onMouseDown={(e) => this.startDraggingPoint(pointObject)}
                        onMouseUp={(e) => this.stopDraggingPoint(pointObject)}
                        text={pointObject.number}
                        fill={this.props.selectedPoint === pointObject ? 'black' : 'yellow'}
                        fontSize={10}
                        fontFamily='Arial'
                        x={frequency - 3}
                        y={this.getGainYPosition(pt.gain) - 5}
                        />
                      </Group>);
            }
        );
    }

    // returns the guide lines that help the user see what
    // frequencies are 100hz, 200hz, 1000hz, etc
    getGuideLines() {
        return GUIDE_FREQUENCIES.map(
            frequency => this.getXPositionForFrequency(frequency))
            .map(x =>
               <Line
                 key={'eq-guideline-' + x}
                 points={[x, 0, x, 160]}
                 stroke='#1f1f1f'
                 strokeWidth={1}
                 closed={false}/>);
    }

    // When the user clicks on an equalizer point, we
    // make note that this point is now selected AND
    // also dragging.
    startDraggingPoint(pt) {
        this.props.setSelectedPoint(pt);
        this.setState({draggingPoint: pt});
    }

    stopDraggingPoint(pt) {
        this.setState({draggingPoint: undefined});
    }

    // When the user moves the mouse and an equalizer point
    // is dragging, then we update the frequency & gain for
    // that point.
    onMouseMove(e) {
        let x = e.evt.layerX;
        let y = e.evt.layerY;
        let frequency = parseInt(this.getFrequencyForXPosition(x));

        if (this.state.draggingPoint !== undefined) {
            this.state.draggingPoint.frequency = frequency;
            this.state.draggingPoint.gain = ((HEIGHT / 2) - y) / 5;
            this.setState(this.state);
        }
    }

    onMouseLeave() {
        this.setState({draggingPoint: undefined});
    }

    render() {
        return (
            <Group
              onMouseMove={(e) => this.onMouseMove(e)}
              onMouseLeave={(e) => this.onMouseLeave()}>
              <Line
                closed={true}
                strokeWidth={2}
                lineCap='round'
                lineJoin='round'
                points={[0, 0, WIDTH, 0, WIDTH, 160, 0, 160]}
                stroke={"black"}
                />
              
              {this.guideLines}
              {this.getEQCurve()}
              {this.getEqualizerPoints()}
            </Group>
        );
    }
}

const mapStateToProps = (state, props) => {
    let pts = props.effect.equalizerPoints;

    let equalizerPoints = {};
    for (let i in pts) {
        let pt = pts[i];
        let frequencyParameter = pt.frequencyParameter;
        let gainParameter = pt.gainParameter;
        let qParameter = pt.qParameter;
        let enabledParameter = pt.enabledParameter;
        equalizerPoints[i] = {
            frequency: state.effects.effectParameters[frequencyParameter.id],
            gain: state.effects.effectParameters[gainParameter.id],
            enabled: state.effects.effectParameters[enabledParameter.id],
            q: state.effects.effectParameters[qParameter.id]};
    }

    return {
        equalizerPoints: equalizerPoints,
    };
};
 
export default connect(mapStateToProps, null)(EQCurve);

